import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../AuthContext';
import Logo from "../../assets/Img/Logo-1.png";
import UserImg from "../../assets/Img/User.gif";
import "../../App.css";

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const IMAGE_BASE_URL = process.env.REACT_APP_API_BASE_PATH || BASE_URL;

export default function WorkerTaskApp() {
  const { user, logout } = useContext(AuthContext);
  const workerId = user?.EmployeeID || '';
  const workerName = user?.Name || '';
  const workerDept = user?.Department || '';
  
  const [hover, setHover] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const [activeTasks, setActiveTasks] = useState([]);
  const [availableLots, setAvailableLots] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('lots');

  const [loading, setLoading] = useState(false);
  const [searchLot, setSearchLot] = useState('');
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [elapsedTimes, setElapsedTimes] = useState({});
  const [remainingTimes, setRemainingTimes] = useState({});

  // Claim/Complete Modals state
  const [claimModalLot, setClaimModalLot] = useState(null);
  const [targetQty, setTargetQty] = useState('');
  
  const [completeModalTask, setCompleteModalTask] = useState(null);
  const [completedQty, setCompletedQty] = useState('');
  const [rejectedQty, setRejectedQty] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [completedPhotoFile, setCompletedPhotoFile] = useState(null);
  const [rejectedPhotoFile, setRejectedPhotoFile] = useState(null);
  const [formError, setFormError] = useState('');
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (workerId && workerDept) {
      fetchData();
    }
  }, [workerId, workerDept]);

  useEffect(() => {
    let interval;
    if (activeTasks.length > 0) {
      interval = setInterval(() => {
        const now = new Date();
        const newElapsed = {};
        const newRemaining = {};
        
        activeTasks.forEach(task => {
          const start = new Date(task.start_time);
          const diff = Math.floor((now - start) / 1000);
          const hrs = String(Math.floor(diff / 3600)).padStart(2, '0');
          const mins = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
          const secs = String(diff % 60).padStart(2, '0');
          newElapsed[task.log_id] = `${hrs}:${mins}:${secs}`;
          
          if (task.exact_due_at) {
            const due = new Date(task.exact_due_at);
            let diffLeft = Math.floor((due - now) / 1000);
            let isOverdue = false;
            if (diffLeft < 0) {
                isOverdue = true;
                diffLeft = Math.abs(diffLeft);
            }
            const lHrs = String(Math.floor(diffLeft / 3600)).padStart(2, '0');
            const lMins = String(Math.floor((diffLeft % 3600) / 60)).padStart(2, '0');
            const lSecs = String(diffLeft % 60).padStart(2, '0');
            newRemaining[task.log_id] = isOverdue ? `-${lHrs}:${lMins}:${lSecs}` : `${lHrs}:${lMins}:${lSecs}`;
          }
        });
        
        setElapsedTimes(newElapsed);
        setRemainingTimes(newRemaining);
      }, 1000);
    } else {
        setElapsedTimes({});
        setRemainingTimes({});
    }
    return () => clearInterval(interval);
  }, [activeTasks]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tasksRes, lotsRes, completedRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/wip-dashboard/worker/lot-qty/active/${workerId}`),
        axios.get(`${BASE_URL}/api/wip-dashboard/worker/available-lots/${workerDept}`),
        axios.get(`${BASE_URL}/api/wip-dashboard/worker/lot-qty/completed/${workerId}`)
      ]);
      
      if (tasksRes.data.success && tasksRes.data.hasActive) {
        setActiveTasks(tasksRes.data.tasks);
      } else {
        setActiveTasks([]);
      }

      if (lotsRes.data.success) {
        setAvailableLots(lotsRes.data.lots);
      }

      if (completedRes.data.success) {
        setCompletedTasks(completedRes.data.tasks);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async (lot) => {
    const result = await Swal.fire({
      title: 'Task Start Karein?',
      html: `Aap <b>${lot.lot_id}</b> lot start karne wale hain.<br/>Kya aap confirm karte hain?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Haan, Start Karo',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      const res = await axios.post(`${BASE_URL}/api/wip-dashboard/worker/lot-qty/claim`, {
        worker_id: workerId,
        worker_name: workerName,
        confirm_time_new_id: lot.confirmTimeNewId,
        lot_id: lot.lot_id
      });

      if (res.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Kaam Shuru!',
          text: res.data.message,
          timer: 2000,
          showConfirmButton: false
        });
        fetchData();
      } else {
        Swal.fire({ icon: 'error', title: 'Oops...', text: res.data.message });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    } finally {
      setLoading(false);
    }
  };

  const completeTask = async () => {
    if (completedQty === '' || completedQty === null) {
      setFormError('Enter completed quantity.');
      return;
    }
    
    const compQty = parseInt(completedQty) || 0;
    const rejQty = parseInt(rejectedQty) || 0;
    const claimedQty = completeModalTask.claimed_qty;

    if (compQty + rejQty !== claimedQty) {
      setFormError(`Completed (${compQty}) + Rejected (${rejQty}) must equal total claimed quantity (${claimedQty}).`);
      return;
    }
    
    setFormError('');

    if (compQty > 0 && !completedPhotoFile) {
      setFormError('Photo evidence is mandatory for completed quantity.');
      return;
    }

    if (rejQty > 0) {
      if (!holdReason || holdReason.trim() === '') {
        setFormError('Hold/Delay Reason is mandatory when there is rejected (wastage) quantity.');
        return;
      }
      if (!rejectedPhotoFile) {
        setFormError('Photo evidence is mandatory because rejected quantity is greater than 0.');
        return;
      }
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('log_id', completeModalTask.log_id);
      formData.append('completed_qty', compQty);
      formData.append('rejected_qty', rejQty);
      if (holdReason) formData.append('hold_reason', holdReason);
      if (completedPhotoFile) formData.append('completed_photo', completedPhotoFile);
      if (rejectedPhotoFile) formData.append('rejected_photo', rejectedPhotoFile);

      const res = await axios.post(`${BASE_URL}/api/wip-dashboard/worker/lot-qty/complete`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        Swal.fire('Completed!', res.data.message, 'success');
        setCompleteModalTask(null);
        setCompletedQty('');
        setRejectedQty('');
        setHoldReason('');
        setCompletedPhotoFile(null);
        setRejectedPhotoFile(null);
        fetchData();
      }
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to complete task', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!workerId) {
    window.location.href = "/";
    return null;
  }

  const filteredLots = availableLots.filter(lot => {
    const matchesSearch = lot.lot_id.toLowerCase().includes(searchLot.toLowerCase());
    const matchesSupervisor = selectedSupervisor ? lot.supervisor === selectedSupervisor : true;
    
    let matchesDate = true;
    if (lot.assigned_date) {
      const d = new Date(lot.assigned_date);
      if (startDate) {
        matchesDate = matchesDate && d >= new Date(startDate);
      }
      if (endDate) {
        const endD = new Date(endDate);
        endD.setHours(23, 59, 59, 999);
        matchesDate = matchesDate && d <= endD;
      }
    }
    return matchesSearch && matchesSupervisor && matchesDate;
  });

  const filteredHistory = completedTasks.filter(task => {
    const matchesSearch = task.lot_id.toLowerCase().includes(searchLot.toLowerCase());
    const matchesSupervisor = selectedSupervisor ? task.supervisor === selectedSupervisor : true;
    
    let matchesDate = true;
    if (task.end_time) {
      const d = new Date(task.end_time);
      if (startDate) {
        matchesDate = matchesDate && d >= new Date(startDate);
      }
      if (endDate) {
        const endD = new Date(endDate);
        endD.setHours(23, 59, 59, 999);
        matchesDate = matchesDate && d <= endD;
      }
    }
    return matchesSearch && matchesSupervisor && matchesDate;
  });

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      {/* NAVBAR */}
      <nav className="navbar navbar-expand-lg bg-body-white" style={{ boxShadow: "0 1px 5px rgba(0, 0, 0, 0.2)", background: "white" }}>
        <div className="container-fluid">
          <a className="navbar-brand" href="/User-dashboard">
            <img src={Logo} className="img-fluid rounded-top" alt="Logo" style={{ height: "40px", marginLeft: "10px" }} />
          </a>
          <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarSupportedContent">
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarSupportedContent">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              <li className="nav-item" style={{ marginLeft: "10px" }}>
                <a className="nav-link active" href="/User-dashboard" style={{ color: "inherit", fontWeight: "600" }}><i className="bi bi-house-door me-1"></i> HOME</a>
              </li>
              <li className="nav-item" style={{ marginLeft: "10px" }}>
                <a className="nav-link active" href="/User-Tickets" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={{ color: hover ? "#ff0000" : "inherit", fontWeight: "600" }}><i className="bi bi-ticket-detailed me-1"></i> TICKET</a>
              </li>
              <li className="nav-item" style={{ marginLeft: "10px" }}>
                <a className="nav-link active" href="/wip-worker-app" style={{ color: "#0ea5e9", fontWeight: "700" }}><i className="bi bi-stopwatch me-1"></i> TASK TRACKER</a>
              </li>
            </ul>
            <div className="d-flex align-items-center ms-lg-auto mt-3 mt-lg-0">
              <span className="me-3" style={{ fontWeight: 600, color: "#475569" }}>
                Welcome, {workerName}!
              </span>
              <div className="dropdown">
                <a className="d-flex align-items-center text-decoration-none dropdown-toggle" role="button" data-bs-toggle="dropdown" style={{ cursor: "pointer" }}>
                  <img src={UserImg} alt="Profile" className="rounded-circle shadow-sm" style={{ width: "40px", height: "40px", objectFit: "cover", border: "2px solid #e2e8f0" }} />
                </a>
                <ul className="dropdown-menu dropdown-menu-end shadow-sm" style={{ border: "none", borderRadius: "12px", marginTop: "10px" }}>
                  <li>
                    <a className="dropdown-item text-danger fw-bold d-flex align-items-center" style={{ cursor: "pointer", padding: "10px 20px" }} onClick={logout}>
                      <i className="bi bi-box-arrow-right me-2"></i> Logout
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mt-4">
        {loading && <div className="text-center py-4"><i className="bi bi-hourglass-split spin fs-2 text-primary"></i></div>}

        {/* ACTIVE TASKS SECTION */}
        {activeTasks.length > 0 && (
          <div className="mb-5">
            <h4 style={{ fontWeight: 800, color: "#0f172a", marginBottom: "16px" }}><i className="bi bi-activity text-primary me-2"></i> Active Tasks</h4>
            <div className="row g-3">
              {activeTasks.map(task => {
                const isSlaBreached = task.status === 'SLA_BREACHED' || task.sla_breached === 1 || task.sla_breached === true;
                return (
                  <div key={task.log_id} className="col-12 col-md-6 col-lg-4">
                    <div style={{ background: "white", borderRadius: "16px", overflow: "hidden", boxShadow: isSlaBreached ? "0 10px 30px rgba(220,38,38,0.18)" : "0 10px 25px rgba(0,0,0,0.05)", border: isSlaBreached ? "2px solid #ef4444" : "1px solid #e2e8f0" }}>
                      
                      {isSlaBreached ? (
                        /* ── SLA BREACHED CARD ─────────────────────────────────── */
                        <div style={{ background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)", padding: "20px", textAlign: "center", color: "white" }}>
                          <div style={{ fontSize: "36px", marginBottom: "4px" }}>⚠️</div>
                          <div style={{ fontSize: "18px", fontWeight: 800, letterSpacing: "-0.5px" }}>TICKET RAISED</div>
                          <div style={{ fontSize: "12px", opacity: 0.85, textTransform: "uppercase", letterSpacing: "1px", marginTop: "4px" }}>
                            {(task.notes && (task.notes.includes('Remediation') || task.notes.includes('Std Time:'))) ? 'Remediation SLA Breached' : '12-Hour SLA Breached'}
                          </div>
                        </div>
                      ) : (task.notes && (task.notes.includes('Remediation') || task.notes.includes('Std Time:'))) ? (
                        /* ── REMEDIATION TASK CARD ─────────────────────────────── */
                        <div style={{ background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)", padding: "20px", textAlign: "center", color: "white" }}>
                          <div style={{ fontSize: "14px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
                            <i className="bi bi-wrench-adjustable me-2"></i>Remediation Task
                          </div>
                          
                          <div className="d-flex justify-content-around align-items-center mt-3">
                            <div>
                              <div style={{ fontSize: "24px", fontWeight: 800, fontFamily: "monospace", letterSpacing: "-1px" }}>
                                {elapsedTimes[task.log_id] || '00:00:00'}
                              </div>
                              <div style={{ fontSize: "10px", opacity: 0.8, textTransform: "uppercase", letterSpacing: "1px" }}>Elapsed Time</div>
                            </div>
                            
                            <div style={{ borderLeft: "1px solid rgba(255,255,255,0.3)", height: "40px" }}></div>
                            
                            <div>
                              <div style={{ fontSize: "24px", fontWeight: 800, fontFamily: "monospace", letterSpacing: "-1px", color: remainingTimes[task.log_id]?.startsWith('-') ? '#fca5a5' : 'white' }}>
                                {remainingTimes[task.log_id] || '00:00:00'}
                              </div>
                              <div style={{ fontSize: "10px", opacity: 0.8, textTransform: "uppercase", letterSpacing: "1px" }}>{remainingTimes[task.log_id]?.startsWith('-') ? 'OVERDUE' : 'Time Left'}</div>
                            </div>
                          </div>
                          
                          {task.notes.match(/Due:\s*([^|]+)/) && (
                            <div style={{ marginTop: "12px", background: "rgba(255,255,255,0.2)", borderRadius: "8px", padding: "6px 12px", fontSize: "13px", fontWeight: 700 }}>
                              <i className="bi bi-clock-history me-1"></i> Due: {task.notes.match(/Due:\s*([^|]+)/)[1].trim()}
                            </div>
                          )}
                        </div>
                      ) : (
                        /* ── NORMAL RUNNING CARD ───────────────────────────────── */
                        <div style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)", padding: "20px", textAlign: "center", color: "white" }}>
                          
                          <div className="d-flex justify-content-around align-items-center">
                            <div>
                              <div style={{ fontSize: "32px", fontWeight: 800, fontFamily: "monospace", letterSpacing: "-1px" }}>
                                {elapsedTimes[task.log_id] || '00:00:00'}
                              </div>
                              <div style={{ fontSize: "12px", opacity: 0.8, textTransform: "uppercase", letterSpacing: "1px" }}>Elapsed Time</div>
                            </div>
                            
                            <div style={{ borderLeft: "1px solid rgba(255,255,255,0.3)", height: "50px" }}></div>
                            
                            <div>
                              <div style={{ fontSize: "24px", fontWeight: 800, fontFamily: "monospace", letterSpacing: "-1px", color: remainingTimes[task.log_id]?.startsWith('-') ? '#fca5a5' : '#e0f2fe' }}>
                                {remainingTimes[task.log_id] || '00:00:00'}
                              </div>
                              <div style={{ fontSize: "10px", opacity: 0.9, textTransform: "uppercase", letterSpacing: "1px" }}>{remainingTimes[task.log_id]?.startsWith('-') ? 'OVERDUE' : 'Time Left (SLA)'}</div>
                            </div>
                          </div>
                          
                        </div>
                      )}

                      <div style={{ padding: "20px" }}>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted fw-bold">Lot ID</span>
                          <span className="fw-bolder">{task.lot_id}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted fw-bold">Supervisor</span>
                          <span className="fw-bolder">{task.supervisor || '-'}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-3">
                          <span className="text-muted fw-bold">Claimed Qty</span>
                          <span className="fw-bolder badge bg-light text-dark fs-6">{task.claimed_qty} pcs</span>
                        </div>

                        {isSlaBreached ? (
                          /* Show warning message, NO finish button */
                          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "12px", padding: "14px", textAlign: "center" }}>
                            <div style={{ color: "#dc2626", fontWeight: 700, fontSize: "14px", marginBottom: "4px" }}>
                              <i className="bi bi-exclamation-triangle-fill me-2"></i>Kaam rok diya gaya hai
                            </div>
                            <div style={{ color: "#7f1d1d", fontSize: "13px" }}>
                              Supervisor <b>{task.supervisor || 'aapke manager'}</b> ko notify kar diya gaya hai. Manager naya worker assign karega.
                            </div>
                          </div>
                        ) : (
                          <button 
                            onClick={() => { setCompleteModalTask(task); setCompletedQty(''); }}
                            className="btn btn-success w-100 fw-bold shadow-sm"
                            style={{ borderRadius: "12px" }}
                          >
                            <i className="bi bi-check-circle-fill me-2"></i> FINISH WORK
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* AVAILABLE LOTS SECTION */}
        <div style={{ background: "white", padding: "24px", borderRadius: "20px", boxShadow: "0 10px 30px rgba(0,0,0,0.03)", border: "1px solid #f1f5f9" }}>
          <ul className="nav nav-pills mb-4" style={{ gap: "10px" }}>
            <li className="nav-item">
              <button 
                className={`nav-link fw-bold ${activeTab === 'lots' ? 'active' : ''}`}
                onClick={() => setActiveTab('lots')}
                style={{ borderRadius: "12px", padding: "10px 24px", transition: "0.2s", background: activeTab === 'lots' ? '#0ea5e9' : '#f8fafc', color: activeTab === 'lots' ? 'white' : '#64748b' }}
              >
                <i className="bi bi-list-task me-2"></i> Available Lots
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link fw-bold ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => setActiveTab('history')}
                style={{ borderRadius: "12px", padding: "10px 24px", transition: "0.2s", background: activeTab === 'history' ? '#10b981' : '#f8fafc', color: activeTab === 'history' ? 'white' : '#64748b' }}
              >
                <i className="bi bi-clock-history me-2"></i> My History (Completed)
              </button>
            </li>
          </ul>

          {/* SHARED FILTERS */}
          <div className="row g-3 mb-4 p-3" style={{ background: "#f8fafc", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)" }}>
            <div className="col-12 mb-0 pb-0">
              <span style={{ fontWeight: 800, color: "#475569", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                <i className="bi bi-funnel-fill text-primary me-1"></i> Filter Tasks
              </span>
            </div>
            
            <div className="col-md-3 col-sm-6">
              <div className="input-group shadow-sm h-100" style={{ borderRadius: "10px", overflow: "hidden", border: "1px solid #cbd5e1" }}>
                <span className="input-group-text bg-light border-0 text-muted px-3" style={{ fontSize: '12px', fontWeight: 700 }}>FROM</span>
                <input 
                  type="date"
                  className="form-control border-0 bg-white"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  style={{ padding: "10px", color: "#475569", fontWeight: 600, fontSize: "14px" }}
                />
              </div>
            </div>
            
            <div className="col-md-3 col-sm-6">
              <div className="input-group shadow-sm h-100" style={{ borderRadius: "10px", overflow: "hidden", border: "1px solid #cbd5e1" }}>
                <span className="input-group-text bg-light border-0 text-muted px-3" style={{ fontSize: '12px', fontWeight: 700 }}>TO</span>
                <input 
                  type="date"
                  className="form-control border-0 bg-white"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  style={{ padding: "10px", color: "#475569", fontWeight: 600, fontSize: "14px" }}
                />
              </div>
            </div>
            
            <div className="col-md-3 col-sm-6">
              <select 
                className="form-select bg-white shadow-sm h-100"
                style={{ padding: "10px 16px", borderRadius: "10px", color: "#475569", fontWeight: 600, border: "1px solid #cbd5e1", fontSize: "14px" }}
                value={selectedSupervisor}
                onChange={e => setSelectedSupervisor(e.target.value)}
              >
                <option value="">All Supervisors</option>
                {[...new Set([...availableLots, ...completedTasks].map(l => l.supervisor))].filter(Boolean).map(sup => (
                  <option key={sup} value={sup}>{sup}</option>
                ))}
              </select>
            </div>
            
            <div className="col-md-3 col-sm-6">
              <div className="input-group shadow-sm h-100" style={{ borderRadius: "10px", overflow: "hidden", border: "1px solid #cbd5e1" }}>
                <span className="input-group-text bg-light border-0 text-muted px-3">
                  <i className="bi bi-search"></i>
                </span>
                <input 
                  type="text" 
                  className="form-control border-0 bg-white ps-2" 
                  placeholder="Search Lot ID..." 
                  value={searchLot}
                  onChange={e => setSearchLot(e.target.value)}
                  style={{ boxShadow: "none", fontWeight: 500, padding: "10px", fontSize: "14px", color: "#475569" }}
                />
              </div>
            </div>
          </div>

          {activeTab === 'lots' && (
            <div>
              <div className="d-flex align-items-center mb-3">
                <h4 style={{ fontWeight: 800, color: "#1e293b", margin: 0, letterSpacing: "-0.5px" }}>
                  <i className="bi bi-list-task text-primary me-2"></i>
                  Available Lots <span className="text-muted">({workerDept})</span>
                </h4>
              </div>

              <div className="table-responsive">
                <table className="table table-hover align-middle mobile-table">
                  <thead className="table-light">
                    <tr>
                      <th className="py-3 text-secondary text-uppercase" style={{ fontSize: "13px" }}>Lot ID</th>
                      <th className="py-3 text-secondary text-uppercase" style={{ fontSize: "13px" }}>Supervisor</th>
                      <th className="py-3 text-secondary text-uppercase" style={{ fontSize: "13px" }}>Available Qty</th>
                      <th className="py-3 text-secondary text-uppercase" style={{ fontSize: "13px" }}>Claimed By</th>
                      <th className="py-3 text-secondary text-uppercase text-end" style={{ fontSize: "13px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLots.length === 0 ? (
                      <tr><td colSpan="5" className="text-center py-4 text-muted">No pending lots available for your department.</td></tr>
                    ) : (
                      filteredLots.map(lot => (
                        <tr key={lot.confirmTimeNewId} className="bg-white">
                          <td className="py-3">
                            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle px-2 py-1 fs-6">
                              {lot.lot_id}
                            </span>
                          </td>
                          <td className="py-3 fw-medium text-dark">{lot.supervisor}</td>
                          <td className="py-3"><span className="badge bg-success bg-opacity-10 text-success fs-6 border border-success border-opacity-25">{lot.availableQty}</span></td>
                          <td data-label="Claimed By">
                            {lot.active_workers && lot.active_workers.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {lot.active_workers.map((w, idx) => (
                                  <span key={idx} className="badge bg-secondary" style={{ fontSize: "11px" }}>
                                    {w.worker_name} ({w.claimed_qty})
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted small">-</span>
                            )}
                          </td>
                          <td className="text-end" data-label="Action">
                            <button 
                              className="btn btn-primary btn-sm px-3 fw-bold rounded-pill shadow-sm"
                              onClick={() => handleClaim(lot)}
                              disabled={loading}
                            >
                              Accept <i className="bi bi-play-fill"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <h4 style={{ fontWeight: 800, color: "#1e293b", margin: 0, letterSpacing: "-0.5px", marginBottom: "20px" }}>
                <i className="bi bi-clock-history text-success me-2"></i>
                My History <span className="text-muted">(Last 20)</span>
              </h4>
              <div className="table-responsive">
                <table className="table table-hover align-middle mobile-table">
                  <thead className="table-light">
                    <tr>
                      <th className="py-3 text-secondary text-uppercase" style={{ fontSize: "13px" }}>Lot ID</th>
                      <th className="py-3 text-secondary text-uppercase" style={{ fontSize: "13px" }}>Dept / Sup</th>
                      <th className="py-3 text-secondary text-uppercase" style={{ fontSize: "13px" }}>Qty Details</th>
                      <th className="py-3 text-secondary text-uppercase" style={{ fontSize: "13px" }}>Photos</th>
                      <th className="py-3 text-secondary text-uppercase" style={{ fontSize: "13px" }}>End Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.length === 0 ? (
                      <tr><td colSpan="5" className="text-center py-4 text-muted">No completed tasks found.</td></tr>
                    ) : (
                      filteredHistory.map(task => (
                        <tr key={task.log_id} className="bg-white">
                          <td className="py-3 fw-bold">{task.lot_id}</td>
                          <td className="py-3">
                            <span className="d-block text-dark fw-medium">{task.department}</span>
                            <span className="text-muted small">{task.supervisor}</span>
                          </td>
                          <td className="py-3">
                            <div className="d-flex flex-wrap gap-2">
                              <span className="badge bg-light text-dark border border-secondary border-opacity-25">Claimed: {task.claimed_qty}</span>
                              <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">Passed: {task.completed_qty}</span>
                              {task.rejected_qty > 0 && <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25">Rejected: {task.rejected_qty}</span>}
                            </div>
                          </td>
                          <td className="py-3">
                            <div className="d-flex flex-column gap-2">
                              {task.photo_url && (
                                <div className="d-flex align-items-center gap-2">
                                  <span style={{ fontSize: "11px", fontWeight: 700, color: "#16a34a", width: "65px" }}>Completed:</span>
                                  <img 
                                    src={`${IMAGE_BASE_URL}${task.photo_url}`} 
                                    alt="Completed" 
                                    style={{ width: "36px", height: "36px", objectFit: "cover", borderRadius: "6px", border: "2px solid #22c55e", cursor: "pointer" }} 
                                    onClick={() => setPreviewImage(`${IMAGE_BASE_URL}${task.photo_url}`)}
                                  />
                                </div>
                              )}
                              {task.rejected_photo_url && (
                                <div className="d-flex align-items-center gap-2">
                                  <span style={{ fontSize: "11px", fontWeight: 700, color: "#dc2626", width: "65px" }}>Rejected:</span>
                                  <img 
                                    src={`${IMAGE_BASE_URL}${task.rejected_photo_url}`} 
                                    alt="Rejected" 
                                    style={{ width: "36px", height: "36px", objectFit: "cover", borderRadius: "6px", border: "2px solid #ef4444", cursor: "pointer" }} 
                                    onClick={() => setPreviewImage(`${IMAGE_BASE_URL}${task.rejected_photo_url}`)}
                                  />
                                </div>
                              )}
                              {!task.photo_url && !task.rejected_photo_url && <span className="text-muted small">-</span>}
                            </div>
                          </td>
                          <td className="py-3 text-muted small">
                            {new Date(task.end_time).toLocaleString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* COMPLETE MODAL */}
      {completeModalTask && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div style={{ background: "white", padding: "32px", borderRadius: "24px", width: "100%", maxWidth: "700px", animation: "slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)", maxHeight: "90vh", overflowY: "auto" }}>
            <div className="d-flex align-items-center mb-3">
              <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a", fontSize: "24px", marginRight: "16px" }}>
                <i className="bi bi-check-circle-fill"></i>
              </div>
              <div>
                <h4 style={{ fontWeight: 800, color: "#0f172a", margin: 0, letterSpacing: "-0.5px" }}>Finish Task</h4>
                <p className="text-muted mb-0" style={{ fontSize: "14px", fontWeight: 500 }}>Lot: {completeModalTask.lot_id}</p>
              </div>
            </div>
            
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "16px", marginBottom: "24px" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted" style={{ fontWeight: 600, fontSize: "13px" }}>CLAIMED QUANTITY</span>
                <span className="badge bg-primary bg-opacity-10 text-primary fs-6 px-3 py-2 border border-primary border-opacity-25 rounded-pill">{completeModalTask.claimed_qty} pcs</span>
              </div>
            </div>
            
            <div className="row">
              <div className="col-md-6 mb-3">
                <label style={{ fontWeight: 700, color: "#334155", marginBottom: "8px", display: "block" }}>Completed Quantity <span className="text-danger">*</span></label>
                <div className="input-group">
                  <span className="input-group-text" style={{ background: "#f1f5f9", border: "2px solid #e2e8f0", borderRight: "none", color: "#94a3b8" }}>
                    <i className="bi bi-box-seam"></i>
                  </span>
                  <input 
                    type="number" 
                    className="form-control" 
                    value={completedQty}
                    onChange={e => setCompletedQty(e.target.value)}
                    placeholder="How many pieces?"
                    max={completeModalTask.claimed_qty}
                    style={{ padding: "12px 16px", border: "2px solid #e2e8f0", borderLeft: "none", fontWeight: 600, color: "#0f172a", boxShadow: "none" }}
                  />
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <label style={{ fontWeight: 700, color: "#334155", marginBottom: "8px", display: "block" }}>Rejected Quantity (Wastage)</label>
                <div className="input-group">
                  <span className="input-group-text" style={{ background: "#fef2f2", border: "2px solid #fecaca", borderRight: "none", color: "#ef4444" }}>
                    <i className="bi bi-trash"></i>
                  </span>
                  <input 
                    type="number" 
                    className="form-control" 
                    value={rejectedQty}
                    onChange={e => setRejectedQty(e.target.value)}
                    placeholder="How many damaged?"
                    style={{ padding: "12px 16px", border: "2px solid #fecaca", borderLeft: "none", fontWeight: 600, color: "#0f172a", boxShadow: "none" }}
                  />
                </div>
              </div>
            </div>

            <label style={{ fontWeight: 700, color: "#334155", marginBottom: "8px", display: "block" }}>
              Hold/Delay Reason {(parseInt(rejectedQty) || 0) > 0 && <span className="text-danger">*</span>}
            </label>
            <div className="input-group mb-3">
              <input 
                type="text" 
                className="form-control" 
                value={holdReason}
                onChange={e => setHoldReason(e.target.value)}
                placeholder="Any reason for delay or defect?"
                style={{ padding: "12px 16px", border: "2px solid #e2e8f0", fontWeight: 600, color: "#0f172a", boxShadow: "none", borderRadius: "8px" }}
              />
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label style={{ fontWeight: 700, color: "#334155", marginBottom: "8px", display: "block" }}>
                  Completed Qty Photo {parseInt(completedQty) > 0 && <span className="text-danger">*</span>}
                </label>
                <div style={{ padding: "8px", border: "2px dashed #94a3b8", borderRadius: "8px", background: "#f8fafc" }}>
                  <input 
                    type="file" 
                    className="form-control border-0 bg-transparent" 
                    accept="image/*"
                    onChange={e => setCompletedPhotoFile(e.target.files[0])}
                    style={{ fontSize: "14px", color: "#475569" }}
                  />
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <label style={{ fontWeight: 700, color: "#334155", marginBottom: "8px", display: "block" }}>
                  Rejected Qty Photo {(parseInt(rejectedQty) || 0) > 0 && <span className="text-danger">*</span>}
                </label>
                <div style={{ padding: "8px", border: "2px dashed #fecaca", borderRadius: "8px", background: "#fef2f2" }}>
                  <input 
                    type="file" 
                    className="form-control border-0 bg-transparent" 
                    accept="image/*"
                    onChange={e => setRejectedPhotoFile(e.target.files[0])}
                    style={{ fontSize: "14px", color: "#ef4444" }}
                  />
                </div>
              </div>
            </div>
            
            {formError && (
              <div className="alert alert-danger py-2 mb-3 mt-2" style={{ fontSize: "14px", fontWeight: "600", borderRadius: "8px" }}>
                <i className="bi bi-exclamation-triangle-fill me-2"></i> {formError}
              </div>
            )}
            
            <div className="d-flex gap-3 mt-3">
              <button 
                className="btn btn-light flex-grow-1 border-0" 
                onClick={() => {
                  setCompleteModalTask(null);
                  setCompletedQty('');
                  setRejectedQty('');
                  setHoldReason('');
                  setCompletedPhotoFile(null);
                  setRejectedPhotoFile(null);
                }}
                style={{ background: "#f1f5f9", color: "#64748b", fontWeight: 700, padding: "14px", borderRadius: "12px" }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-success flex-grow-1 border-0 shadow-sm" 
                disabled={!completedQty || loading} 
                onClick={completeTask}
                style={{ background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)", fontWeight: 700, padding: "14px", borderRadius: "12px" }}
              >
                {loading ? <i className="bi bi-hourglass-split spin"></i> : 'SUBMIT'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* IMAGE PREVIEW MODAL */}
      {previewImage && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15, 23, 42, 0.8)", backdropFilter: "blur(4px)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }} onClick={() => setPreviewImage(null)}>
          <div style={{ position: "relative", maxWidth: "90%", maxHeight: "90%" }}>
            <button 
              onClick={() => setPreviewImage(null)}
              style={{ position: "absolute", top: "-15px", right: "-15px", background: "white", color: "#0f172a", border: "none", borderRadius: "50%", width: "36px", height: "36px", fontSize: "18px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
            <img src={previewImage} alt="Preview" style={{ maxWidth: "100%", maxHeight: "85vh", borderRadius: "12px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }} onClick={(e) => e.stopPropagation()} />
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); scale: 0.95; }
          to { opacity: 1; transform: translateY(0); scale: 1; }
        }
        .form-control:focus {
          border-color: #3b82f6 !important;
          box-shadow: none !important;
        }
        .input-group-text {
          transition: all 0.2s ease-in-out;
        }
        .form-control:focus + .input-group-text,
        .input-group:focus-within .input-group-text {
          border-color: #3b82f6 !important;
          color: #3b82f6 !important;
        }
        @media (max-width: 768px) {
          .mobile-table thead {
            display: none;
          }
          .mobile-table tbody tr {
            display: block;
            margin-bottom: 16px;
            background: white;
            border-radius: 12px;
            box-shadow: 0 4px 10px rgba(0,0,0,0.05);
            padding: 16px;
            border: 1px solid #e2e8f0;
          }
          .mobile-table tbody td {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            border-bottom: 1px dashed #e2e8f0;
            text-align: right;
          }
          .mobile-table tbody td:last-child {
            border-bottom: none;
            justify-content: center;
            padding-top: 16px;
          }
          .mobile-table tbody td::before {
            content: attr(data-label);
            font-weight: 700;
            font-size: 13px;
            color: #64748b;
            text-transform: uppercase;
            text-align: left;
            margin-right: 20px;
          }
          .mobile-w-auto {
            width: 100% !important;
          }
        }
        @media (min-width: 769px) {
          .mobile-w-auto {
            width: auto !important;
          }
        }
      `}</style>
    </div>
  );
}
